/**
 * Modern Mini Cart JavaScript
 * Swipe gestures, undo functionality, and enhanced interactions
 * Imperial Candles Theme Enhancement
 */

(function() {
  'use strict';

  // ============================================
  // CART SWIPE HANDLER (Mobile)
  // ============================================

  class CartSwipeHandler {
    constructor(cartElement) {
      this.cart = cartElement;
      this.startX = 0;
      this.currentX = 0;
      this.startY = 0;
      this.isDragging = false;
      this.threshold = 120; // pixels to trigger close
      this.verticalThreshold = 30; // max vertical movement

      // Only enable on mobile/tablet
      if (window.innerWidth <= 1024) {
        this.init();
      }
    }

    init() {
      this.cart.addEventListener('touchstart', this.handleStart.bind(this), { passive: true });
      this.cart.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
      this.cart.addEventListener('touchend', this.handleEnd.bind(this));
    }

    handleStart(e) {
      const touch = e.touches[0];

      // Only start swipe from left edge (first 60px)
      if (touch.clientX > 60) return;

      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.isDragging = true;
      this.cart.style.transition = 'none';
    }

    handleMove(e) {
      if (!this.isDragging) return;

      this.currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;

      const diffX = this.currentX - this.startX;
      const diffY = Math.abs(currentY - this.startY);

      // Ignore if primarily vertical scroll
      if (diffY > this.verticalThreshold && diffY > Math.abs(diffX)) {
        this.isDragging = false;
        return;
      }

      // Only allow right swipe
      if (diffX > 0) {
        e.preventDefault();
        const translateX = Math.min(diffX, window.innerWidth);
        this.cart.style.transform = `translateX(${translateX}px)`;

        // Add opacity fade
        const opacity = 1 - (translateX / window.innerWidth) * 0.5;
        this.cart.style.opacity = opacity;
      }
    }

    handleEnd() {
      if (!this.isDragging) return;

      const diff = this.currentX - this.startX;
      this.cart.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';

      if (diff > this.threshold) {
        // Close cart
        this.cart.style.transform = `translateX(${window.innerWidth}px)`;
        this.cart.style.opacity = '0';

        setTimeout(() => {
          document.body.classList.remove('cart-sidebar-show');
          this.cart.style.transform = '';
          this.cart.style.opacity = '';
          this.cart.style.transition = '';
        }, 300);
      } else {
        // Reset position
        this.cart.style.transform = '';
        this.cart.style.opacity = '';
      }

      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
    }
  }

  // ============================================
  // UNDO REMOVE FUNCTIONALITY
  // ============================================

  class UndoRemove {
    constructor() {
      this.undoWindow = 5000; // 5 seconds
      this.pendingRemoval = null;
      this.init();
    }

    init() {
      // Listen for remove button clicks
      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-cart-remove], .cart-remove');
        if (!removeBtn) return;

        // Check if this is inside the cart sidebar
        const cartSidebar = removeBtn.closest('.halo-sidebar-wrapper, .modern-sidecart');
        if (!cartSidebar) return;

        e.preventDefault();
        e.stopPropagation();

        const itemRow = removeBtn.closest('[data-item-row], .modern-cart-item, .previewCartItem');
        if (!itemRow) return;

        // Get item info
        const itemId = removeBtn.dataset.cartRemoveId || removeBtn.dataset.id;
        const itemLine = removeBtn.dataset.line || this.getLineNumber(itemRow);

        this.initiateRemoval(itemId, itemLine, itemRow);
      }, true); // Use capture phase
    }

    getLineNumber(itemRow) {
      // Try to find line number from quantity input
      const qtyInput = itemRow.querySelector('[data-line], [name="updates[]"]');
      if (qtyInput) {
        return qtyInput.dataset.line || qtyInput.getAttribute('data-line');
      }
      return null;
    }

    initiateRemoval(itemId, itemLine, itemRow) {
      // Cancel any pending removal
      if (this.pendingRemoval) {
        clearTimeout(this.pendingRemoval.timeout);
      }

      // Store item data for potential restoration
      const qtyInput = itemRow.querySelector('[data-cart-quantity], .quantity');
      const itemData = {
        variantId: qtyInput ? qtyInput.dataset.cartQuantityId : null,
        quantity: qtyInput ? qtyInput.value : 1,
        html: itemRow.cloneNode(true)
      };

      // Visual feedback
      itemRow.style.opacity = '0.4';
      itemRow.style.pointerEvents = 'none';
      itemRow.classList.add('removing');

      // Show undo toast
      const toast = this.showUndoToast();

      // Set removal timeout
      this.pendingRemoval = {
        itemId,
        itemLine,
        itemRow,
        itemData,
        toast,
        timeout: setTimeout(() => {
          this.finalizeRemoval(itemLine, itemRow, toast);
        }, this.undoWindow)
      };
    }

    showUndoToast() {
      const toast = document.createElement('div');
      toast.className = 'cart-undo-toast';
      toast.innerHTML = `
        <div class="cart-undo-toast__content">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
            <path d="M4 10h12M4 10l4 4m-4-4l4-4" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span class="cart-undo-toast__text">Item removed</span>
          <button class="cart-undo-toast__btn" type="button" data-undo-action>
            Undo
          </button>
        </div>
        <div class="cart-undo-toast__progress"></div>
      `;

      document.body.appendChild(toast);

      // Progress bar animation
      const progress = toast.querySelector('.cart-undo-toast__progress');
      progress.style.animation = `undo-countdown ${this.undoWindow}ms linear`;

      // Undo button handler
      toast.querySelector('[data-undo-action]').addEventListener('click', () => {
        this.handleUndo();
      });

      // Show toast
      requestAnimationFrame(() => {
        toast.classList.add('show');
      });

      return toast;
    }

    handleUndo() {
      if (!this.pendingRemoval) return;

      const { itemRow, toast, timeout } = this.pendingRemoval;

      // Cancel removal
      clearTimeout(timeout);

      // Restore item visually
      itemRow.style.opacity = '1';
      itemRow.style.pointerEvents = 'auto';
      itemRow.classList.remove('removing');

      // Remove toast
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);

      // Clear pending
      this.pendingRemoval = null;

      // Show success message
      this.showSimpleToast('Item restored', 'success');
    }

    async finalizeRemoval(itemLine, itemRow, toast) {
      try {
        // Remove from Shopify cart
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            line: itemLine,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error('Remove failed');
        }

        const cart = await response.json();

        // Remove from DOM with animation
        itemRow.style.height = itemRow.offsetHeight + 'px';
        itemRow.style.transition = 'all 0.3s ease-out';
        requestAnimationFrame(() => {
          itemRow.style.height = '0';
          itemRow.style.margin = '0';
          itemRow.style.padding = '0';
          itemRow.style.opacity = '0';
        });

        setTimeout(() => itemRow.remove(), 300);

        // Update cart count
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
        });

        // Update cart total
        const cartTotalElements = document.querySelectorAll('[data-cart-subtotal], [data-cart-subtotal-mobile], [data-cart-subtotal-desktop]');
        cartTotalElements.forEach(el => {
          const money = el.querySelector('.money') || el;
          money.textContent = this.formatMoney(cart.total_price);
        });

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

        // Remove toast
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 300);

        // Reload cart if needed
        if (typeof halo !== 'undefined' && typeof halo.updateSidebarCart === 'function') {
          halo.updateSidebarCart(cart);
        }

      } catch (error) {
        console.error('Remove failed:', error);
        // Restore on error
        itemRow.style.opacity = '1';
        itemRow.style.pointerEvents = 'auto';
        itemRow.classList.remove('removing');
        toast.remove();
        this.showSimpleToast('Failed to remove item', 'error');
      }

      this.pendingRemoval = null;
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, window.theme.moneyFormat || '£{{amount}}');
      }
      return '£' + (cents / 100).toFixed(2);
    }

    showSimpleToast(message, type = 'success') {
      const toast = document.createElement('div');
      toast.className = `cart-toast ${type}`;

      const icon = type === 'success'
        ? '<svg class="cart-toast__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>'
        : '<svg class="cart-toast__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>';

      toast.innerHTML = `
        ${icon}
        <span class="cart-toast__message">${message}</span>
      `;

      document.body.appendChild(toast);

      setTimeout(() => {
        toast.classList.add('hiding');
        setTimeout(() => toast.remove(), 300);
      }, 2000);
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function initModernCart() {
    // Initialize swipe handler (mobile only)
    const cartSidebar = document.getElementById('halo-cart-sidebar');
    if (cartSidebar && window.innerWidth <= 1024) {
      new CartSwipeHandler(cartSidebar);
    }

    // Initialize undo remove
    new UndoRemove();

    // Add smooth scrolling to cart items
    const cartItems = document.querySelector('.modern-sidecart__items, .halo-sidebar-wrapper');
    if (cartItems) {
      cartItems.style.scrollBehavior = 'smooth';
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModernCart);
  } else {
    initModernCart();
  }

  // Re-initialize on cart updates
  document.addEventListener('cart:updated', initModernCart);

  // Expose for external use
  window.ModernCart = {
    init: initModernCart
  };

})();
